// Core Modules
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';

// External Modules
import { NgxPermissionsService } from 'ngx-permissions';

// Models
import { NAV_GROUPS, NavGroup } from './nav.model';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  imports: [RouterLink, RouterLinkActive],
  host: { '[class.mobile-open]': 'mobileOpen()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  private readonly permissionsService = inject(NgxPermissionsService);

  /** Whether the nav is shown as an open off-canvas drawer on small screens. */
  public readonly mobileOpen = input<boolean>(false);

  /** Emits when a nav item is chosen, so the shell can close the mobile drawer. */
  public readonly navigate = output<void>();

  private readonly permissions = toSignal(this.permissionsService.permissions$, { initialValue: this.permissionsService.getPermissions() });

  /** Labels of groups the user has collapsed. */
  private readonly collapsedGroups = signal<ReadonlySet<string>>(new Set());

  /** Labelled groups reduced to the items the user may see (empty groups dropped). */
  public readonly visibleGroups = computed<NavGroup[]>(() => {
    const perms = this.permissions();
    return NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => !!perms[item.permission]) })).filter((group) => group.items.length > 0);
  });

  /**
   * Whether a group is currently collapsed.
   * @param label group label
   * @returns true when collapsed
   */
  public isCollapsed(label: string): boolean {
    return this.collapsedGroups().has(label);
  }

  /**
   * Toggle a group's collapsed state.
   * @param label group label
   */
  public toggleGroup(label: string): void {
    const next = new Set(this.collapsedGroups());
    if (next.has(label)) {
      next.delete(label);
    } else {
      next.add(label);
    }
    this.collapsedGroups.set(next);
  }
}
